import { EventSearchFilter } from '../models/event-filter';
import { EventFilterType } from '../models/event-filter-type-enum';
import { DateUtil } from '../../shared/utils/date.util';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UISearchStatusMappingEnum } from '../models/ui-search-status-mapping-enum';
import { OrderedSet } from 'immutable';
import { EventMenuSearchFilter } from '../models/event-menu-search-filter';

@Injectable()
export class EventFilterUtilsService {
  static readonly dateRangeValueSeparator = ';';

  /**
   * Parses 2 dates represented as strings and returns the value to store in the EventFilterType.DATE_RANGE filter
   * @param date1
   * @param date2
   * @returns {string}
   */
  static getDateRangeFilterValue(date1: string, date2: string): string {
    return DateUtil.formatDate(date1) + this.dateRangeValueSeparator + DateUtil.formatDate(date2);
  }

  static parseFilterValueAsDateRange(dateRangeString: string): string[] {
    return dateRangeString.split(this.dateRangeValueSeparator);
  }

  constructor(private translateService: TranslateService) {
  }

  /**
   * This method returns a key to identify what kind of token to make.
   * This is useful to ensure that two filters with the same key can't be present at the same time.
   * @param filter
   * @returns string
   */
  getUniqueTokenKey(filter: EventSearchFilter): string {
    // only 1 date filter is allowed at a time
    if (filter.filterField === EventFilterType.DATE_RANGE.toString() ||
      filter.filterField === EventFilterType.DATE_BEFORE.toString() ||
      filter.filterField === EventFilterType.DATE_AFTER.toString()) {
      return EventFilterType.DATE_RANGE.toString();
    } else if (filter.filterField === EventFilterType.GUEST_NAME.toString() ||
      filter.filterField === EventFilterType.PAYMENT_STATUS.toString()) {
      // these filters allow for for multiple tokens
      return filter.filterField + ':' + filter.filterValue;
    }
    // by default, there can only be 1 instance of a filter token at a time
    return filter.filterField;
  }

  /**
   * Returns a string representation of a given Date that will be displayed in a search filter token
   * @param date
   * @returns {string}
   */
  getDateStringForToken(date: Date): string {
    return (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
  }

  /**
   * This method will return the string used to display the value of the filter in a token
   * @param filter
   * @returns {string}
   */
  getTokenDisplayString(filter: EventSearchFilter): string {
    if (filter.filterField === EventFilterType.COMPLETED.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_COMPLETED');
    } else if (filter.filterField === EventFilterType.MISSING_AGENCY.toString()) {
      return this.translateService.instant('EVENTS.EVENT_STATUS_MISSING_AGENCY');
    } else if (filter.filterField === EventFilterType.DATE_RANGE.toString()) {
      const strings = filter.filterValue.split(EventFilterUtilsService.dateRangeValueSeparator);
      if (strings.length === 2) {
        return this.getDateStringForToken(DateUtil.parseDate(strings[0])) + ' - ' +
          this.getDateStringForToken(DateUtil.parseDate(strings[1]));
      } else {
        return 'error';
      }
    } else if (filter.filterField === EventFilterType.DATE_BEFORE.toString() ||
      filter.filterField === EventFilterType.DATE_AFTER.toString()) {
      return this.getDateStringForToken(DateUtil.parseDate(filter.filterValue));
    } else if (filter.filterField === EventFilterType.PAYMENT_STATUS.toString()) {
      return this.labelForPaymentStatusValue(filter.filterValue);
    } else {
      return filter.filterValue;
    }
  }

  labelForPaymentStatusValue(filterValue: string): string {
    let label = filterValue;

    if (filterValue === UISearchStatusMappingEnum.PAID.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAID';
    } else if (filterValue === UISearchStatusMappingEnum.UNPAID.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.UNPAID';
    } else if (filterValue === UISearchStatusMappingEnum.PAYMENT_IN_PROCESS.toString()) {
      label = 'EVENTS.PAYMENT_STATUS_FILTER_VALUE.PAYMENT_IN_PROCESS';
    }

    return this.translateService.instant(label);
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/service/event-filter-util-service.ts